/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview State and handlers for the B2B participating company form.
 * @module hooks/useB2BParticipatingCompanyForm
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { getCustomerCompanyUsersByCompanyId } from "../logic/customerCompanyUser";
import {
  emptySchedule,
  getScheduleValidation,
  hasScheduleValidationError,
  normalizeCustomerUser,
} from "../utils/participantScheduleValidation.js";

/**
 * @param {Object} company
 * @returns {string}
 */
export const getCompanyLabel = (company) =>
  company?.nombreComercial || company?.razonSocial || company?.nombre || "";

/**
 * @param {Object} option
 * @param {string} rawInput
 * @returns {boolean}
 */
export const filterCompanyOption = (option, rawInput) => {
  const input = rawInput.toLowerCase().trim();
  if (!input) {
    return true;
  }
  const company = option.data ?? option;
  const commercial = (company.nombreComercial || "").toLowerCase();
  const legal = (company.razonSocial || "").toLowerCase();
  const name = (company.nombre || "").toLowerCase();
  return (
    commercial.includes(input) || legal.includes(input) || name.includes(input)
  );
};

/**
 * Encapsulates participating company form state, validation, and submit flow.
 * @param {Object} props - Same props as `B2BEventParticipatingCompanyForm`
 * @returns {Object}
 */
const useB2BParticipatingCompanyForm = ({
  active,
  variant = "modal",
  eventDateStart,
  eventDateEnd,
  eventTimeStart,
  eventTimeEnd,
  participatingCompanies,
  buyerCompanies,
  selectedParticipant,
  error,
  onCancel,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isEditMode = Boolean(selectedParticipant);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedCustomerUsers, setSelectedCustomerUsers] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [companyActive, setCompanyActive] = useState(false);

  const eventBounds = useMemo(
    () => ({ eventDateStart, eventDateEnd, eventTimeStart, eventTimeEnd }),
    [eventDateStart, eventDateEnd, eventTimeStart, eventTimeEnd],
  );

  const availableCompanies = useMemo(
    () =>
      buyerCompanies?.filter(
        (company) =>
          !participatingCompanies?.some(
            (participant) => participant.idEmpresaCompradora === company._id,
          ),
      ) ?? [],
    [buyerCompanies, participatingCompanies],
  );

  useEffect(() => {
    if (!active) {
      setSelectedCompany(null);
      setCompanyUsers([]);
      setSelectedCustomerUsers([]);
      setCompanyActive(false);
    }
  }, [active]);

  useEffect(() => {
    if (active && selectedParticipant && buyerCompanies?.length) {
      const company = buyerCompanies.find(
        (entry) => entry._id === selectedParticipant.idEmpresaCompradora,
      );
      if (company) {
        setSelectedCompany(company);
      }
      const initialCustomerUsers =
        selectedParticipant.ejecutivosCompradores ?? [];
      setSelectedCustomerUsers(initialCustomerUsers.map(normalizeCustomerUser));
      setCompanyActive(selectedParticipant.activo !== false);
    }
  }, [active, selectedParticipant, buyerCompanies]);

  useEffect(() => {
    if (!selectedCompany?._id) {
      setCompanyUsers((prev) => (prev.length === 0 ? prev : []));
      if (!isEditMode) {
        setSelectedCustomerUsers((prev) => (prev.length === 0 ? prev : []));
      }
      return () => {};
    }

    let cancelled = false;
    setLoadingUsers(true);
    getCustomerCompanyUsersByCompanyId(selectedCompany._id, navigate).then(
      (users) => {
        if (!cancelled) {
          setCompanyUsers(Array.isArray(users) ? users : []);
          if (!isEditMode) {
            setSelectedCustomerUsers([]);
          }
        }
        setLoadingUsers(false);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [selectedCompany?._id, navigate, isEditMode]);

  const isUserSelected = useCallback(
    (userId) => selectedCustomerUsers.some((entry) => entry._id === userId),
    [selectedCustomerUsers],
  );

  const hasAnyValidationError = useMemo(
    () =>
      selectedCustomerUsers.some((entry) =>
        hasScheduleValidationError(getScheduleValidation(entry, eventBounds)),
      ),
    [selectedCustomerUsers, eventBounds],
  );

  const getSchedulesForUser = useCallback(
    (userId) =>
      selectedCustomerUsers
        .filter((entry) => entry._id === userId)
        .map((entry) => ({
          fecha: entry.fecha ?? "",
          horaInicio: entry.horaInicio ?? "",
          horaFin: entry.horaFin ?? "",
          horaInicioComida: entry.horaInicioComida ?? "",
          horaFinComida: entry.horaFinComida ?? "",
        })),
    [selectedCustomerUsers],
  );

  const setScheduleForUserAt = useCallback((userId, scheduleIndex, updates) => {
    setSelectedCustomerUsers((prev) => {
      let count = 0;
      for (let index = 0; index < prev.length; index += 1) {
        if (prev[index]._id === userId) {
          if (count === scheduleIndex) {
            const next = [...prev];
            next[index] = { ...next[index], ...updates };
            return next;
          }
          count += 1;
        }
      }
      return prev;
    });
  }, []);

  const addScheduleForUser = useCallback((userId) => {
    setSelectedCustomerUsers((prev) => [
      ...prev,
      { _id: userId, ...emptySchedule() },
    ]);
  }, []);

  const removeScheduleForUser = useCallback((userId, scheduleIndex) => {
    setSelectedCustomerUsers((prev) => {
      let count = 0;
      for (let index = 0; index < prev.length; index += 1) {
        if (prev[index]._id === userId) {
          if (count === scheduleIndex) {
            return prev.filter((_, idx) => idx !== index);
          }
          count += 1;
        }
      }
      return prev;
    });
  }, []);

  const handleCheckboxChange = useCallback(
    (user) => {
      const userId = user._id || user.id;
      if (isUserSelected(userId)) {
        setSelectedCustomerUsers((prev) =>
          prev.filter((entry) => entry._id !== userId),
        );
      } else {
        addScheduleForUser(userId);
      }
    },
    [addScheduleForUser, isUserSelected],
  );

  const handleCompanySelectChange = useCallback((option) => {
    setSelectedCompany(option);
    setCompanyActive(false);
  }, []);

  const handleCompanyActiveChange = useCallback((event) => {
    setCompanyActive(event.target.checked);
  }, []);

  const getCompanyOptionValue = useCallback((option) => option._id, []);

  const getNoCompaniesMessage = useCallback(
    () => t("pages.b2bEventForm.noCompaniesToAdd", "No companies to add"),
    [t],
  );

  const handleConfirm = useCallback(async () => {
    if (!selectedCompany) {
      return;
    }
    setIsSaving(true);
    try {
      await Promise.resolve(
        onSuccess(
          selectedCompany,
          selectedCustomerUsers,
          isEditMode,
          companyActive,
        ),
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    selectedCompany,
    selectedCustomerUsers,
    isEditMode,
    companyActive,
    onSuccess,
  ]);

  return {
    t,
    active,
    variant,
    error,
    onCancel,
    isEditMode,
    selectedCompany,
    companyUsers,
    loadingUsers,
    selectedCustomerUsers,
    isSaving,
    companyActive,
    availableCompanies,
    hasAnyValidationError,
    isUserSelected,
    getSchedulesForUser,
    handleCheckboxChange,
    setScheduleForUserAt,
    removeScheduleForUser,
    addScheduleForUser,
    handleCompanySelectChange,
    handleCompanyActiveChange,
    getCompanyOptionValue,
    getNoCompaniesMessage,
    handleConfirm,
    eventDateStart,
    eventDateEnd,
    eventTimeStart,
    eventTimeEnd,
  };
};

export default useB2BParticipatingCompanyForm;
